import {
	CreateDrawerFormProps,
	FormDrawerContentProps,
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nOnlyOnMainForm,
	I18nTextField,
	TextField,
	UpdateDrawerFormProps,
	useDynamicConfig,
	validateEachLanguage,
} from "@dgs/core";
import { QueryFunction, QueryKey } from "@tanstack/react-query";
import { FormikErrors } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	DataFieldOptionFormValues,
	DataFieldOptionTranslationEntry,
	DataFieldOptionUpdateResource,
} from "../../../shared/types/dataFieldOption";

const useValidate = () => {
	const { t } = useTranslation();
	return (values: DataFieldOptionFormValues) => {
		const errors: FormikErrors<DataFieldOptionFormValues> = {};

		if (!values.key) {
			errors.key = t("Required field");
		}

		return {
			...errors,
			...validateEachLanguage(values.translations, (_, values) => {
				const errors: { [K in keyof DataFieldOptionTranslationEntry]?: any } = {};

				if (!values.value) {
					errors.value = t("Required field");
				}

				return errors;
			}),
		};
	};
};

const DataFieldOptionFormContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();
	return (
		<I18nFormDrawerContent {...props} emptyTranslation={{ value: "", order: 0 }}>
			<I18nOnlyOnMainForm>
				<TextField name="key" label={t("Key")} required={true} />
			</I18nOnlyOnMainForm>
			<I18nTextField name="value" label={t("Value")} required={true} />
		</I18nFormDrawerContent>
	);
};

export const DataFieldOptionCreateDrawer: FC<CreateDrawerFormProps<DataFieldOptionFormValues>> = (props) => {
	const { t } = useTranslation();
	const { languageSettings } = useDynamicConfig();

	const emptyDataFieldOption: DataFieldOptionFormValues = useMemo(
		() => ({
			key: "",
			translations: [
				{
					locale: languageSettings.fallback,
					value: "",
					order: 0,
				},
			],
		}),
		[languageSettings.fallback],
	);

	const validate = useValidate();

	return (
		<I18nFormDrawer
			{...props}
			title={t("Edit data field option")}
			validate={validate}
			initialValues={emptyDataFieldOption}
			renderForm={(props) => <DataFieldOptionFormContent {...props} />}
		/>
	);
};

interface UpdateProps extends UpdateDrawerFormProps<DataFieldOptionFormValues> {
	queryFn: QueryFunction<DataFieldOptionUpdateResource>;
	queryKey: QueryKey;
}

export const DataFieldOptionDrawer: FC<UpdateProps> = (props) => {
	const { t } = useTranslation();

	const validate = useValidate();

	return (
		<I18nFormFetchDrawer
			{...props}
			validate={validate}
			title={t("Edit data field option")}
			renderSuccess={(props) => <DataFieldOptionFormContent {...props} />}
		/>
	);
};
